import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bold, Eraser, Italic, List } from 'lucide-react';
import type { QuoteRichBlock } from '../../types';
import {
  QUOTE_RICH_COLORS,
  QUOTE_RICH_DEFAULT_COLOR,
  parseQuoteRichDom,
  quoteRichToHtml,
} from '../../services/quoteRichText';

interface Props {
  /** Startinhalt. Danach fuehrt der Editor selbst (unkontrolliert) — fuer eine
   *  andere Position mit neuem `key` einhaengen. */
  initial: QuoteRichBlock[];
  onChange: (blocks: QuoteRichBlock[]) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * Kleiner Formatier-Editor fuer die Leistungsbeschreibung einer
 * Angebotsposition: Fett, Kursiv, Aufzaehlung, Farbe.
 *
 * contentEditable + execCommand statt einer Editor-Bibliothek: vier Befehle
 * rechtfertigen keine neue Abhaengigkeit. Der Inhalt wird nach jeder Eingabe
 * ueber parseQuoteRichDom in Bloecke uebersetzt — das DOM selbst wird nie
 * gespeichert. Der Editor ist bewusst unkontrolliert: den DOM bei jedem
 * Tastendruck aus dem Zustand neu zu setzen, liesse den Cursor springen.
 */
export const QuoteRichTextEditor: React.FC<Props> = ({ initial, onChange, disabled, autoFocus }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState({ bold: false, italic: false, list: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = quoteRichToHtml(initial);
    if (autoFocus && !disabled) {
      el.focus();
      // Cursor ans Ende statt an den Anfang.
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
    // Nur beim Einhaengen: siehe Komponentenkommentar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(() => {
    if (ref.current) onChange(parseQuoteRichDom(ref.current));
  }, [onChange]);

  const refreshActive = useCallback(() => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) return;
    setActive({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      list: document.queryCommandState('insertUnorderedList'),
    });
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshActive);
    return () => document.removeEventListener('selectionchange', refreshActive);
  }, [refreshActive]);

  const exec = (cmd: string, arg?: string) => {
    if (disabled || !ref.current) return;
    ref.current.focus();
    // <b>/<i>/<font> statt style-Attributen: beides liest der Parser, Tags
    // sind aber stabiler beim Wieder-Aufheben.
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(cmd, false, arg);
    emit();
    refreshActive();
  };

  // Eingefuegt wird nur Text: fremdes HTML (Word, Webseiten) brächte Schriften
  // und Farben mit, die das PDF nicht kennt.
  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (text) document.execCommand('insertText', false, text);
  };

  const toolBtn = (label: string, on: boolean, onClick: () => void, icon: React.ReactNode) => (
    <button
      type="button"
      // mousedown verhindern: sonst verliert der Editor Fokus und Auswahl,
      // bevor der Befehl ankommt.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`p-1.5 rounded-lg transition-all ${on ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}
      title={label}
      aria-label={label}
      aria-pressed={on}
    >
      {icon}
    </button>
  );

  return (
    <div className="flex flex-col gap-2">
      {!disabled && (
        <div className="flex flex-wrap items-center gap-1 rounded-xl border border-slate-200 bg-white px-2 py-1">
          {toolBtn('Fett (Strg+B)', active.bold, () => exec('bold'), <Bold className="w-4 h-4" />)}
          {toolBtn('Kursiv (Strg+I)', active.italic, () => exec('italic'), <Italic className="w-4 h-4" />)}
          {toolBtn('Aufzählung', active.list, () => exec('insertUnorderedList'), <List className="w-4 h-4" />)}
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden />
          <span className="text-[11px] font-semibold text-slate-400 mr-0.5">Farbe</span>
          {QUOTE_RICH_COLORS.map((c) => (
            <button
              key={c.label}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec('foreColor', c.value ?? QUOTE_RICH_DEFAULT_COLOR)}
              className="w-5 h-5 rounded-full border border-slate-300 hover:scale-110 transition-transform"
              style={{ backgroundColor: c.value ?? QUOTE_RICH_DEFAULT_COLOR }}
              title={c.label}
              aria-label={`Farbe ${c.label}`}
            />
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden />
          {toolBtn('Formatierung entfernen', false, () => exec('removeFormat'), <Eraser className="w-4 h-4" />)}
        </div>
      )}
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Leistungsbeschreibung"
        onInput={emit}
        onPaste={onPaste}
        onDrop={(e) => e.preventDefault()}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        style={{ color: QUOTE_RICH_DEFAULT_COLOR }}
        className={`min-h-[9rem] max-h-[55vh] overflow-y-auto whitespace-pre-wrap break-words px-3 py-2.5 rounded-xl border border-slate-200 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-bold ${disabled ? 'bg-slate-50 opacity-80' : 'bg-white'}`}
      />
    </div>
  );
};
